// import type { File } from 'buffer';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAPI } from 'services/axios';
import useToast from 'services/responseToast';

const getAllMessages = () => {
  return useAPI({
    method: 'GET',
    url: `/messages`,
  });
};

export const useGetMessagesQuery = () => {
  const responseToast = useToast();
  const fetchAllMessages = localStorage.getItem('FetchAllMessages');
  const fetchAllMessagesParsed = fetchAllMessages
    ? JSON.parse(fetchAllMessages)
    : false;
  console.log(fetchAllMessagesParsed);
  return useQuery(['ALL_MESSAGES'], getAllMessages, {
    refetchInterval:
      !!localStorage.getItem('AUTH_TOKEN') && fetchAllMessagesParsed && 15000,
  });
};

const getMessage = (id: number) => {
  return useAPI({
    method: 'GET',
    url: `/messages/${id}`,
  });
};

export const useGetMessageQuery = (id: number) => {
  const responseToast = useToast();
  return useQuery([`MESSAGES_BY_${id}`], () => getMessage(id), {
    // refetchInterval: 15000,
  });
};

const reclassifyRequest = (payload: any) => {
  const { id, data } = payload;
  return useAPI({
    method: 'POST',
    url: `/messages/${id}/reclassify`,
    data,
  });
};

export const useReclassifyQuery = () => {
  const responseToast = useToast();
  const queryClient = useQueryClient();
  return useMutation(reclassifyRequest, {
    onSuccess: (res: any) => {
      queryClient.invalidateQueries(['ALL_MESSAGES']);
      responseToast.toastSuccess('Request reclassified successfully');
    },
    onError: (error: any) =>
      responseToast.toastError(error, 'something went wrong'),
  });
};

const forwardRequest = (payload: any) => {
  const { id, data } = payload;
  return useAPI({
    method: 'POST',
    url: `/messages/${id}/forward`,
    data,
  });
};

export const useForwardQuery = () => {
  const responseToast = useToast();
  return useMutation(forwardRequest, {
    onSuccess: () =>
      responseToast.toastSuccess('Request forwarded successfully'),
    onError: (error: any) =>
      responseToast.toastError(error, 'something went wrong'),
  });
};

const addUserRequest = (payload: any) => {
  const { id, data } = payload;
  return useAPI({
    method: 'POST',
    url: `/messages/${id}/staff`,
    data,
  });
};

export const useAddUserQuery = () => {
  const responseToast = useToast();
  return useMutation(addUserRequest, {
    onSuccess: () => responseToast.toastSuccess('User added successfully'),
    onError: (error: any) =>
      responseToast.toastError(error, 'something went wrong'),
  });
};

const acknowledgeRequest = (data: any) => {
  return useAPI({
    method: 'POST',
    url: `/messages/acknowledge`,
    data,
  });
};

export const useAcknowledgeRequestQuery = () => {
  const queryClient = useQueryClient();
  const responseToast = useToast();
  return useMutation(acknowledgeRequest, {
    onSuccess: (res: any) => {
      queryClient.invalidateQueries(['ALL_MESSAGES']);
      responseToast.toastSuccess('Request acknowledged successfully');
    },
    onError: (error: any) =>
      responseToast.toastError(error, 'something went wrong'),
  });
};

const replyRequest = (payload: any) => {
  const { id, formData } = payload;
  return useAPI({
    method: 'POST',
    url: `/messages/${id}/reply`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: formData,
  });
};

export const useReplyRequestQuery = () => {
  const queryClient = useQueryClient();
  const responseToast = useToast();
  return useMutation(replyRequest, {
    onError: (error: any) =>
      responseToast.toastError(error, 'something went wrong'),
  });
};

const createNewMessage = (payload: any) => {
  const { formData } = payload;
  return useAPI({
    method: 'POST',
    url: `/messages`,
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const useCreateNewMessageQuery = () => {
  const responseToast = useToast();
  return useMutation(createNewMessage, {
    onSuccess: () => responseToast.toastSuccess('Request created successfully'),
    onError: (error: any) =>
      responseToast.toastError(error, 'something went wrong'),
  });
};

const markRead = (payload: any) => {
  const { id, data } = payload;
  return useAPI({
    method: 'POST',
    url: `/messages/mark-read`,
    data,
  });
};

export const useMarkReadQuery = () => {
  const responseToast = useToast();
  return useMutation(markRead, {
    onError: (error: any) =>
      responseToast.toastError(error, 'something went wrong'),
  });
};

const getPatientMessages = (id: number) => {
  return useAPI({
    method: 'GET',
    url: `/messages/patient/${id}`,
  });
};

export const useGetPatientMessagesQuery = (id: number) => {
  return useQuery([`PATIENT_MESSAGE_BY_${id}`], () => getPatientMessages(id), {
    refetchInterval: 15000,
  });
};

const archiveRequest = (payload: any) => {
  const { id } = payload;
  return useAPI({
    method: 'POST',
    url: `/messages/archive/${id}`,
  });
};

export const useArchiveRequestQuery = () => {
  const responseToast = useToast();
  return useMutation(archiveRequest, {
    onSuccess: () =>
      responseToast.toastSuccess('Request archived successfully'),
    onError: (error: any) =>
      responseToast.toastError(error, 'something went wrong'),
  });
};

const unArchiveRequest = (payload: any) => {
  const { id } = payload;
  return useAPI({
    method: 'POST',
    url: `/messages/unarchive/${id}`,
  });
};

export const useUnArchiveRequestQuery = () => {
  const responseToast = useToast();
  return useMutation(unArchiveRequest, {
    onSuccess: () =>
      responseToast.toastSuccess('Request unArchived successfully'),
    onError: (error: any) =>
      responseToast.toastError(error, 'something went wrong'),
  });
};

const sendChatMessage = (payload: any) => {
  const { id, content, files } = payload;
  const formData = new FormData();

  formData.append('content', content);

  if (files && files.length > 0) {
    files.forEach((file: File) => formData.append('files', file));
  }

  return useAPI({
    method: 'POST',
    url: `/messages/${id}/reply`,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  });
};

export const useSendChatMessage = () => {
  return useMutation(sendChatMessage, {
    onSuccess: (data, variables) => {
      // Optional: Handle global success here
    },
    onError: (error) => {
      // Optional: Handle global errors here
    },
  });
};

const updateUnreadMessage = (payload: any) => {
  const { userUuids } = payload;
  return useAPI({
    method: 'POST',
    url: '/users/unread-message',
    data: { userUuids },
  });
};

export const useUpdateUnreadMessage = () => {
  return useMutation(updateUnreadMessage);
};

interface StaffCreateNewMessageProps {
  patient_id: string;
  email: string;
  description: string;
  files: any[];
  textLine: string;
  message_type: string;
}

const staffCreateNewMessage = ({
  description,
  files,
  textLine,
  patient_id,
  email,
  message_type,
}: StaffCreateNewMessageProps) => {
  const formData = new FormData();

  formData.append('patient_id', patient_id);
  formData.append('description', description);
  formData.append('title', textLine);
  formData.append('email', email);
  formData.append('message_type', message_type);

  if (files && files.length > 0) {
    files.forEach((file: File) => formData.append('files', file));
  }

  return useAPI({
    url: `messages/create-by-subject`,
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const useStaffCreateNewMessage = () => {
  return useMutation(staffCreateNewMessage);
};

const sendInviteUser = (payload: any) => {
  const { email, patient_id } = payload;
  return useAPI({
    method: 'POST',
    url: `/messages/invitepatient/${email}/${patient_id}`,
  });
};

export const useSendInviteUser = () => {
  return useMutation(sendInviteUser);
};

const searchUser = (payload: any) => {
  const { accountId, name } = payload;
  return useAPI({
    method: 'GET',
    url: `/messages/search-users?accountId=${accountId}&searchString=${name}`,
  });
};

export const useSearchUser = () => {
  return useMutation(searchUser);
};
